import { ComponentPropsWithoutRef, FC } from 'react'
import { Bell, CheckCircle, CircleX, Info, X } from 'lucide-react'

import { cn } from '@/utils'

export type ToastMessageProps = {
  type?: 'error' | 'success' | 'loading' | 'info' | 'notification'
  title?: string
  message?: string
  onClick?: () => void
  closeToast?: () => void
} & ComponentPropsWithoutRef<'div'>

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Loading = 'loading',
  Info = 'info',
  Notification = 'notification',
}

export const ToastMessage: FC<ToastMessageProps> = ({
  className,
  type,
  children,
  title,
  message,
  onClick,
  closeToast,
}) => {
  let borderColor1 = 'rgba(107, 114, 128, 0.1)'
  if (type === ToastType.Success) {
    borderColor1 = 'rgba(7, 148, 85, 0.1)'
  } else if (type === ToastType.Error) {
    borderColor1 = 'rgba(239, 68, 69, 0.1)'
  } else if (type === ToastType.Notification) {
    borderColor1 = '#fef4e6'
  }

  let borderColor2 = 'rgba(107, 114, 128, 0.3)'
  if (type === ToastType.Success) {
    borderColor2 = 'rgba(7, 148, 85, 0.3)'
  } else if (type === ToastType.Error) {
    borderColor2 = 'rgba(239, 68, 69, 0.3)'
  } else if (type === ToastType.Notification) {
    borderColor2 = '#fddeb5'
  }

  return (
    <>
      <div
        className="rounded-full border-2 border-solid size-[38px]"
        style={{
          borderColor: borderColor1,
          padding: 3,
        }}
      >
        <div
          className="rounded-full border-2 border-solid size-[28px] flex-[0_0_38px]"
          style={{
            borderColor: borderColor2,
            padding: 2,
          }}
        >
          {type === ToastType.Success && (
            <CheckCircle
              size={20}
              className="text-foreground-fg-success-primary"
            />
          )}
          {type === ToastType.Error && (
            <CircleX size={20} className="text-foreground-fg-error-primary" />
          )}
          {type === ToastType.Info && (
            <Info size={20} className="text-foreground-fg-tertiary-600" />
          )}
          {type === ToastType.Notification && (
            <Bell size={20} className="text-foreground-fg-warning-secondary" />
          )}
        </div>
      </div>
      {type === ToastType.Notification ? (
        <button
          className={cn(
            'flex flex-col flex-1 pl-2 bg-transparent cursor-pointer text-left'
          )}
          onClick={onClick}
        >
          <div className="flex-1 text-sm font-semibold text-foreground-fg-primary-900 ">
            {title}
          </div>
          <div className="text-tertiary-600 text-sm">{message}</div>
        </button>
      ) : (
        <div
          className={cn(
            'flex-1 text-sm font-semibold text-foreground-fg-primary-900 pl-2',
            className
          )}
        >
          {children}
        </div>
      )}

      <button
        className="size-9 flex bg-transparent items-center justify-center flex-[0_0_36px]"
        onClick={(e) => {
          e.stopPropagation()
          closeToast?.()
        }}
      >
        <X size={20} className="text-foreground-fg-quaternary-400" />
      </button>
    </>
  )
}
